import { yupResolver } from "@hookform/resolvers/yup";
import {
  Box,
  Button,
  FormControl,
  FormHelperText,
  Grid,
  IconButton,
  MenuItem,
  Select,
  Typography,
} from "@material-ui/core";
import AddIcon from "@material-ui/icons/Add";
import HighlightOffIcon from "@material-ui/icons/HighlightOff";
import { postSocialNetworks } from "adapters";
import { DialogWrapper } from "components/shared/dialog";
import { Input } from "components/shared/input";
import style from "components/url-desinger/social-network/social-network.module.scss";
import { useEffect, useState } from "react";
import { Controller, useFieldArray, useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { GetUrlBuilderData, SetUrlBuilderData } from "store/url-builder";
import * as yup from "yup";
import { SocialProvider } from "..";
import { getSocialIcon } from "../socials-list";

const schema = yup.object().shape({
  socials: yup.array().of(
    yup.object().shape({
      networkName: yup.string().trim().required().label("Social Network"),
      url: yup.string().trim().required().label("URL"),
    })
  ),
});

interface SocialField {
  networkName: string;
  url: string;
}

interface FormValues {
  socials: SocialField[];
}

interface AddSocialDialogProps {
  isOpen: boolean;
  onClose(): void;
  builderType: string;
}

export const AddSocialDialog = ({
  isOpen,
  onClose,
  builderType,
}: AddSocialDialogProps) => {
  const builderData = useSelector(GetUrlBuilderData);
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const { cardId }: { cardId: string } = useParams();

  const {
    control,
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: yupResolver(schema),
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: "socials",
  });

  const addNewSocial = () => append({ networkName: "", url: "" });

  const removeSocial = (idx: number) => remove(idx);

  const submit = (data: FormValues) => {
    setLoading(true);

    if (Object.keys(errors).length) return;

    const socials = data.socials?.map((field: any) => {
      return {
        networkName: field.networkName,
        url: field.url,
      };
    });

    postSocialNetworks(cardId, builderType, socials)
      .then(({ data }) => {
        if (!data.success) return;
        dispatch(SetUrlBuilderData(data.data));
        onClose();
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (builderData.socialNetworks.length > 0) {
      // Filter out Skype entries
      const filteredSocials = builderData.socialNetworks.filter(
        (social) => social.networkName !== "Skype" && social.networkName !== "Skype ID"
      );
      if (filteredSocials.length > 0) {
        append(filteredSocials);
      }
    }
  }, [builderData.socialNetworks]);

  return (
    <DialogWrapper
      open={isOpen}
      onClose={(event, reason) => {
        if (reason && reason === "backdropClick") return false;
        else onClose();
      }}
      title="Social Networks"
      loading={loading}
      onSubmit={handleSubmit(submit)}
    >
      <Box
        style={{
          maxHeight: "70vh",
          overflowY: "auto",
          overflowX: "hidden",
          width: "100%",
          paddingRight: "8px",
        }}
      >
        {fields.map((social, index) => (
          <Grid container item xs={12} className="mb-3" key={social.id}>
            <Grid xs className="mr-2">
              <Controller<FormValues>
                control={control}
                name={`socials.${index}.networkName` as const}
                key={social.id}
                render={({ field: { value, onChange } }) => (
                  <FormControl fullWidth>
                    <Typography variant="subtitle2">
                      Select Social Network
                    </Typography>
                    <Select
                      classes={{ root: style.socialSelect }}
                      labelId="providers-label"
                      id="providers"
                      disabled={loading}
                      value={value === "LinkedIn" ? "Linkedin" : value}
                      error={
                        errors.socials && !!errors?.socials[index]?.networkName
                      }
                      onChange={onChange}
                    >
                      {Object.keys(SocialProvider).map((key, index) => {
                        const sProvider = (SocialProvider as any)[key];
                        return (
                          <MenuItem value={sProvider} key={index}>
                            <Box display="inline-flex" alignItems="center">
                              <Box marginRight="5px" display="inline-flex">
                                {getSocialIcon(sProvider, {
                                  width: 24,
                                  height: 24,
                                })}
                              </Box>
                              {sProvider}
                            </Box>
                          </MenuItem>
                        );
                      })}
                    </Select>
                    <FormHelperText error>
                      {(errors?.socials &&
                        errors.socials[index]?.networkName?.message) ||
                        ""}
                    </FormHelperText>
                  </FormControl>
                )}
              />
            </Grid>
            <Grid xs>
              <Controller<FormValues>
                control={control}
                name={`socials.${index}.url` as const}
                key={social.id}
                render={({ field: { value, onChange } }) => (
                  <Input
                    label="URL"
                    error={errors.socials && !!errors?.socials[index]?.url}
                    helperText={
                      (errors?.socials && errors.socials[index]?.url?.message) ||
                      ""
                    }
                    fullWidth
                    disabled={loading}
                    value={value}
                    onChange={onChange}
                  />
                )}
              />
            </Grid>

            <Grid
              item
              container
              alignItems={
                errors.socials &&
                  (!!errors?.socials[index]?.networkName ||
                    !!errors?.socials[index]?.url)
                  ? "center"
                  : "flex-end"
              }
              xs={1}
            >
              <IconButton onClick={() => removeSocial(index)} className="mb-1">
                <HighlightOffIcon color="error" />
              </IconButton>
            </Grid>
          </Grid>
        ))}
        <Grid item container xs={12} justifyContent="center">
          <Button onClick={addNewSocial} style={{ textTransform: "none" }}>
            <AddIcon /> Add New Social Network
          </Button>
        </Grid>
      </Box>
    </DialogWrapper>
  );
};
